import Checkbox from "antd/es/checkbox/Checkbox"
import { EditOutlined, FileTextOutlined, FormOutlined } from "@ant-design/icons"

import { useState, useRef, useEffect } from "react"
import { getFieldCategory } from "../template/TemplateHelper"
import { getFormDataSetFieldValue, formatSsnValue, isFieldExisting, updateFormDataSet, formatDecimalValue } from "./util/CrmFieldUtil"
import InputField from "./InputField"
import { Button, Popconfirm, Tabs  } from 'antd'
import CrmInputsError from "./CrmInputsError"
import { SSN_FIELDS, RETIRED_RELATED_FIELDS, INVESTMENT_EXP_FIELDS, DOB_INVESTMENT_EXP_FIELDS, ENTITY_ADDRESS_FIELDS, TRUST_ADDRESS_FIELDS, DECIMAL_FIELDS } from "./util/Constants"
import { formatDate, formatNumber, isNumber } from "../../utils/Utilities"
import ClientSelectorAlert from "./ClientSelectorAlert"
import CrmFormGeneratorModal from "./CrmFormGeneratorModal.js"
import { selectCurrentRole } from "../../auth/authSlice.js"
import { useSelector } from "react-redux"
import { getFieldTypes } from "../template/TemplateHelper"
import FormDocuSignIndividualRequest from "../../form/FormDocuSignIndividualRequest.js"
import { applyClientConditionalValues, applyAccountConditionalValues, applyPrimaryAddressIfSameAsEntityAddress, applyPrimaryAddressIfSameAsTrustAddress } from "./util/CrmConditionalValuesUtil.js"

const CrmInputs = ({ crmId, clientAccountsInfos, clientOptions, broker, handleUpdate, setClientAccountsInfos, selectedClient, isUpdating, handleDeleteClient, handleDeleteAccount, formErrorList, refreshClientAccountsInfo, fieldOptions }) => {

    const [clientOrAccountsHeaderValue, setClientOrAccountsHeaderValue] = useState("CI")
    const [showOnlyMissing, setShowOnlyMissing] = useState(false)
    const [isInfoDirty, setIsInfoDirty] = useState(false)
    const [generateFormModalOpen, setGenerateFormModalOpen] = useState(false)
    const [isRequestDocuSignFormModalOpen, setRequestDocuSignFormModalOpen] = useState(false)


    const updateFileRef = useRef(null)
    const role = useSelector(selectCurrentRole)

    useEffect(() => {
        setClientOrAccountsHeaderValue("CI")
    }, [selectedClient?.clientId])

    useEffect(() => {
        if(clientAccountsInfos && !isInfoDirty) {
            if(clientOrAccountsHeaderValue === "CI") {
                applyResidentStateValueToLegalState(clientAccountsInfos.clientFormDataSet)
                applyDefaultValueForRetired(clientAccountsInfos.clientFormDataSet)
                applyDefaultInvestmentExpValues(clientAccountsInfos.clientFormDataSet)
            } else {
                let accountFormDataSet = clientAccountsInfos?.accounts.filter(account => account.accountId === clientOrAccountsHeaderValue)[0]?.accountFormDataSet
                if(accountFormDataSet) {
                    applyInitialEntityAddressValue(clientAccountsInfos.clientFormDataSet, accountFormDataSet)
                    applyInitialTrustAddressValue(clientAccountsInfos.clientFormDataSet, accountFormDataSet)
                }
            }
            
        }

    }, [clientAccountsInfos, isInfoDirty, clientOrAccountsHeaderValue])


    useEffect(() => {
        const doAutoUpdate = async () => {
            if(clientAccountsInfos && isInfoDirty) {
                handleUpdate(true)
                setIsInfoDirty(false)
            }
        }

        const interval = setInterval(doAutoUpdate, 5000)
        return () => clearInterval(interval)
    }, [clientAccountsInfos, isInfoDirty])

    const applyResidentStateValueToLegalState = (clientInfos) => {
        let residentStateValue = getFormDataSetFieldValue(clientInfos, "Resident State")
        let legalStateValue = getFormDataSetFieldValue(clientInfos, "Legal State")

        if(!residentStateValue?.trim()) {
            return
        }

        if(residentStateValue?.trim() === legalStateValue?.trim()) {
            return
        }

        let updatedClientFormDataSet = updateFormDataSet(residentStateValue, "Legal State", clientInfos)
        setClientAccountsInfos((prevClientAccountsInfos) => ({...prevClientAccountsInfos, clientFormDataSet: updatedClientFormDataSet}))
        setIsInfoDirty(true)
    }

    const applyDefaultValueForRetired = (clientInfos) => {
        let retiredValue = getFormDataSetFieldValue(clientInfos, "Retired?")
        let positionValue = getFormDataSetFieldValue(clientInfos, "Position")
        if(!(retiredValue === "Y" || positionValue?.toLowerCase() === "retired")) {
            return
        }

        let isUpdated = false
        let updatedClientFormDataSet = clientInfos
        RETIRED_RELATED_FIELDS.forEach(field => {
            let fieldValue = getFormDataSetFieldValue(updatedClientFormDataSet, field)
            if(isFieldExisting(updatedClientFormDataSet, field) && fieldValue !== "N/A") {
                updatedClientFormDataSet = updateFormDataSet("N/A", field, updatedClientFormDataSet)
                isUpdated = true
            }
        })

        if(isUpdated) {
            setClientAccountsInfos((prevClientAccountsInfos) => ({...prevClientAccountsInfos, clientFormDataSet: updatedClientFormDataSet}))
            setIsInfoDirty(true)
        }
    }

    const applyInitialEntityAddressValue = (clientFormDataSet, accountFormDataSet) => {
        let triggerFieldName = "Entity Address same as Primary Address?"
        let triggerFieldValue = getFormDataSetFieldValue(accountFormDataSet, triggerFieldName)

        let isUpdated = false
        let updatedAccountFormDataSet = applyPrimaryAddressIfSameAsEntityAddress(triggerFieldValue, triggerFieldName, accountFormDataSet, clientFormDataSet, broker)

        ENTITY_ADDRESS_FIELDS.forEach(field => {
            let oldFieldValue = getFormDataSetFieldValue(accountFormDataSet, field)
            let newFieldValue = getFormDataSetFieldValue(updatedAccountFormDataSet, field)
            if(oldFieldValue !== newFieldValue) {
                isUpdated = true
            }
        })

        if(isUpdated) {
            let updatedAccounts = clientAccountsInfos.accounts.map(account => {
                let updatedAccount = account
                if(clientOrAccountsHeaderValue == account.accountId) {
                    updatedAccount = {...updatedAccount, accountFormDataSet: updatedAccountFormDataSet}
                }
                return updatedAccount
            })
            setClientAccountsInfos((prevClientAccountsInfos) => ({...prevClientAccountsInfos, accounts: updatedAccounts}))
            setIsInfoDirty(true)
        }
    }

    const applyInitialTrustAddressValue = (clientFormDataSet, accountFormDataSet) => {
        let triggerFieldName = "Trust Address same as Primary Address?"
        let triggerFieldValue = getFormDataSetFieldValue(accountFormDataSet, triggerFieldName)

        let isUpdated = false
        let updatedAccountFormDataSet = applyPrimaryAddressIfSameAsTrustAddress(triggerFieldValue, triggerFieldName, accountFormDataSet, clientFormDataSet, broker)

        TRUST_ADDRESS_FIELDS.forEach(field => {
            let oldFieldValue = getFormDataSetFieldValue(accountFormDataSet, field)
            let newFieldValue = getFormDataSetFieldValue(updatedAccountFormDataSet, field)
            if(oldFieldValue !== newFieldValue) {
                isUpdated = true
            }
        })

        if(isUpdated) {
            let updatedAccounts = clientAccountsInfos.accounts.map(account => {
                let updatedAccount = account
                if(clientOrAccountsHeaderValue == account.accountId) {
                    updatedAccount = {...updatedAccount, accountFormDataSet: updatedAccountFormDataSet}
                }
                return updatedAccount
            })
            setClientAccountsInfos((prevClientAccountsInfos) => ({...prevClientAccountsInfos, accounts: updatedAccounts}))
            setIsInfoDirty(true)
        }
    }

    const applyDefaultInvestmentExpValues = (clientInfos) => {
        let dateOfBirthValue = getFormDataSetFieldValue(clientInfos, "Date of Birth")
        let formattedDob = dateOfBirthValue ? formatDate(dateOfBirthValue) : "Invalid date"

        let isUpdated = false
        let updatedClientFormDataSet = clientInfos
        INVESTMENT_EXP_FIELDS.concat(DOB_INVESTMENT_EXP_FIELDS).forEach(field => {
            let fieldValue = getFormDataSetFieldValue(updatedClientFormDataSet, field)
            let toBeUpdated = isFieldExisting(updatedClientFormDataSet, field) && (!fieldValue || !fieldValue.toString().trim() || fieldValue === '0')
            let updatedValue = computeInvestmentExpValue(field, formattedDob)

            if(toBeUpdated && fieldValue !== updatedValue) {
                updatedClientFormDataSet = updateFormDataSet(updatedValue, field, updatedClientFormDataSet)
                isUpdated = true
            } 
        })

        if(isUpdated) {
            setClientAccountsInfos((prevClientAccountsInfos) => ({...prevClientAccountsInfos, clientFormDataSet: updatedClientFormDataSet}))
            setIsInfoDirty(true)
        }
    }

    const applyJointAccountValues = (jointClientAccountsInfos) => {
        let isUpdated = false
        let updatedAccountFormDataSet = clientAccountsInfos?.accounts.filter(account => account.accountId === clientOrAccountsHeaderValue)[0]?.accountFormDataSet

        let jointAccountFields = getFieldCategory(broker)["Joint Account Information"]
        jointAccountFields.forEach(jointField => {
            let clientField = jointField.replace("Joint".toLowerCase(), "")?.trim()
            if(clientField === "Length of Employment".toLowerCase()) {
                clientField = "Length of Employment (yrs)"
            }

            let fieldValue = getFormDataSetFieldValue(updatedAccountFormDataSet, jointField)
            let updatedFieldValue = getFormDataSetFieldValue(jointClientAccountsInfos.clientFormDataSet, clientField)
            
            if(isFieldExisting(updatedAccountFormDataSet, jointField) && fieldValue !== updatedFieldValue) {
                updatedAccountFormDataSet = updateFormDataSet(updatedFieldValue, jointField, updatedAccountFormDataSet)
                isUpdated = true
            }
        })

        if(isUpdated) {
            let updatedAccounts = clientAccountsInfos.accounts.map(account => {
                let updatedAccount = account
                if(clientOrAccountsHeaderValue == account.accountId) {
                    updatedAccount = {...updatedAccount, accountFormDataSet: updatedAccountFormDataSet}
                }
                return updatedAccount
            })
            setClientAccountsInfos((prevClientAccountsInfos) => ({...prevClientAccountsInfos, accounts: updatedAccounts}))
            setIsInfoDirty(true)
        }
    }

    const applyBeneficiaryValues = (beneficiaryClientAccountsInfos, beneficiaryHeader) => {
        let isUpdated = false
        let updatedAccountFormDataSet = clientAccountsInfos?.accounts.filter(account => account.accountId === clientOrAccountsHeaderValue)[0]?.accountFormDataSet

        let beneficiaryFields = getFieldCategory(broker)[beneficiaryHeader]
        beneficiaryFields.forEach(beneficiaryField => {
            let clientField = beneficiaryField.replace(`${beneficiaryHeader?.toLowerCase()}: `, "")?.toLowerCase().trim()
            if(clientField === "SSN".toLowerCase()) {
                clientField = "Social Security Number"
            } else if(clientField === "Birth Date".toLowerCase()) {
                clientField = "Date of Birth"
            }

            let fieldValue = getFormDataSetFieldValue(updatedAccountFormDataSet, beneficiaryField)
            let updatedFieldValue = getFormDataSetFieldValue(beneficiaryClientAccountsInfos.clientFormDataSet, clientField)
            
            if(isFieldExisting(updatedAccountFormDataSet, beneficiaryField) && fieldValue !== updatedFieldValue) {
                updatedAccountFormDataSet = updateFormDataSet(updatedFieldValue, beneficiaryField, updatedAccountFormDataSet)
                isUpdated = true
            }
        })

        if(isUpdated) {
            let updatedAccounts = clientAccountsInfos.accounts.map(account => {
                let updatedAccount = account
                if(clientOrAccountsHeaderValue == account.accountId) {
                    updatedAccount = {...updatedAccount, accountFormDataSet: updatedAccountFormDataSet}
                }
                return updatedAccount
            })
            setClientAccountsInfos((prevClientAccountsInfos) => ({...prevClientAccountsInfos, accounts: updatedAccounts}))
            setIsInfoDirty(true)
        }
    }

    const computeInvestmentExpValue = (field, dateOfBirth) => {
        let value = "0"
        if(DOB_INVESTMENT_EXP_FIELDS.includes(field)) {
            if(dateOfBirth && dateOfBirth !== "Invalid date") {
                const d = new Date(dateOfBirth)
                const year = d.getFullYear()
                if(year > 1500) {
                    value = (new Date().getFullYear() - (year + 25)).toString()
                }
            }
        }
        return value
    }

    const getInputFilterSwitch = () => {
        return (
            <div className="settings-checkbox">
                <table>
                    <tr>
                        <td><Checkbox value={showOnlyMissing} checked={showOnlyMissing} onChange={() => setShowOnlyMissing(!showOnlyMissing)} /></td>
                        <td><label>Only missing info</label></td>
                    </tr>
                </table>
            </div>
        )
    }

    const handleClientOrAccountsHeaderSelect = (value) => {
        setClientOrAccountsHeaderValue(value)
    }

    const onInputChange = (clientOrAccountId, name, value, type) => {
        if(SSN_FIELDS.includes(name)) {
            value = formatSsnValue(value)
        }

        if(DECIMAL_FIELDS.includes(name)) {
            value = formatDecimalValue(value)
        }

        if(value && type === 'number' && isNumber(value)) {
            value = formatNumber(value)
        }

        let updatedClientFormDataSet = updateFormDataSet(value, name, clientAccountsInfos.clientFormDataSet)
        if(clientOrAccountId === "CI") {
            updatedClientFormDataSet = applyClientConditionalValues(value, name, updatedClientFormDataSet, broker)
            setClientAccountsInfos((prevClientAccountsInfos) => ({...prevClientAccountsInfos, clientFormDataSet: updatedClientFormDataSet}))
        } else {
            let updatedAccounts = clientAccountsInfos.accounts.map(account => {
                let updatedAccount = account
                if(account.accountId == clientOrAccountId) {
                    let updatedAccountInfos = updateFormDataSet(value, name, account.accountFormDataSet)
                    updatedAccountInfos = applyAccountConditionalValues(value, name, updatedAccountInfos, updatedClientFormDataSet, broker)

                    updatedAccount = {...updatedAccount, accountFormDataSet: updatedAccountInfos}
                }
                return updatedAccount
            })
            setClientAccountsInfos((prevClientAccountsInfos) => ({...prevClientAccountsInfos, accounts: updatedAccounts}))
        }

        setIsInfoDirty(true)
    }

    const createClientOrAccountsHeaderSelect = () => {
        let items = []
        let isClientMissingValues = isMissingValue(clientAccountsInfos?.clientFormDataSet)

        items.push({
            label: <span className="tab-label">{(isClientMissingValues ? " *" : "") + "Client Information"}</span>,
            key: "CI"
        })

        clientAccountsInfos?.accounts &&
            clientAccountsInfos.accounts.forEach(account => {
                let accountInfos = account.accountFormDataSet
                if(!accountInfos) {
                    return
                }

                let isMissingValues = isMissingValue(accountInfos)
                let accountType = accountInfos.filter(field => field.formFieldName === "Account Type")[0]?.formFieldValue || ""
                let accountNumber = accountInfos.filter(field => field.formFieldName === "Account Number")[0]?.formFieldValue || ""

                items.push({
                    onChange: {handleClientOrAccountsHeaderSelect},
                    label: <span className="tab-label">{(isMissingValues ? " *" : "") + accountType + " - " + accountNumber}</span>,
                    key: account.accountId
                })
            })
        
        return items
    }

    const isFieldHidden = (field) => {
        let fieldName = field.formFieldName?.toLowerCase().trim()
        let fieldTypes = getFieldTypes(broker)
        
        return fieldTypes[fieldName]?.hidden
    }

    const isFieldPrerequisiteNotMet = (field) => {
        let fieldName = field.formFieldName?.toLowerCase().trim()
        let fieldTypes = getFieldTypes(broker)
        
        let prerequisite = fieldTypes[fieldName]?.prerequisite

        if(!prerequisite) {
            return
        }

        let inputs = clientOrAccountsHeaderValue === "CI" ? clientAccountsInfos?.clientFormDataSet : 
            clientAccountsInfos?.accounts.filter(account => account.accountId === clientOrAccountsHeaderValue)[0]?.accountFormDataSet

        let actualValue = getFormDataSetFieldValue(inputs, prerequisite.field)

        return !prerequisite.values?.includes(actualValue?.trim())
    }

    const createInputs = () => {
        let inputs = clientOrAccountsHeaderValue === "CI" ? clientAccountsInfos?.clientFormDataSet : 
            clientAccountsInfos?.accounts.filter(account => account.accountId === clientOrAccountsHeaderValue)[0]?.accountFormDataSet
        if(!inputs) {
            return
        }

        let categorySection = {}
        inputs.forEach(input => {
            if(isFieldHidden(input)) {
                return
            }

            if(isFieldPrerequisiteNotMet(input)) {
                return
            }
            
            let fieldName = input.formFieldName?.toLowerCase().trim()
            let isOthers = true
            let fieldCategory = getFieldCategory(broker)
            Object.keys(fieldCategory).forEach(header => {
                if(fieldCategory[header].includes(fieldName)) {
                    let section = categorySection[header] ? categorySection[header] : []
                    section.push(input)
                    
                    categorySection[header] = section  
                    isOthers = false
                }
            })
            
            if(isOthers) {
                let section = categorySection["Other Information"] ? categorySection["Other Information"] : []
                section.push(input)
                
                categorySection["Other Information"] = section  
            }
        })

        const onUpdate = (e) => {
            e.preventDefault()
            handleUpdate()
            setIsInfoDirty(false)
        }

        return (
            <div>
                <form className="input-form" onSubmit={(e) => onUpdate(e)}>
                    {formErrorList && formErrorList.length > 0 && <CrmInputsError errorList={formErrorList} />}
                    {
                        inputs && inputs.length > 0 ?
                            <Tabs
                                defaultActiveKey="0"
                                type="card"
                                items={Object.keys(categorySection).sort((a, b) => {
                                        let otherInformationSection = "Other Information"
                                        if (a === otherInformationSection) return 1
                                        if (b === otherInformationSection) return -1
                                        return 0
                                    }).map((header, index) => {
                                        const id = String(index)
                                        return {
                                            label: <span className="tab-label">{ (isMissingValue(categorySection[header]) ? '*' : '') + header}</span>,
                                            key: id,
                                            children: createSection(categorySection, header),
                                        }
                                })}
                            />
                        :
                            <p>No info needed...</p>
                    }
                    <button style={{visibility: "hidden"}} type="submit" ref={updateFileRef}></button>
                </form>
            </div>
        )
    }

    const isMissingValue = (inputs) => {
        if(!showOnlyMissing) {
            return false
        }

        return inputs?.some(curr => curr.formFieldValue == null || curr.formFieldValue === "")
    }

    const createSection = (categorySection, header) => {
        return (
            <>
                {header === "Joint Account Information" && 
                    <ClientSelectorAlert 
                        message={"Note: You may select an existing client for joint account information."}
                        modalHeader={"Joint Account Client"}
                        clientOptions={clientOptions}
                        onClientSelect={(jointClientAccountsInfos) => applyJointAccountValues(jointClientAccountsInfos)}
                    />
                }

                {isBeneficiarySection(header) && 
                    <ClientSelectorAlert 
                        message={`Note: You may select an existing client for beneficiary ${header.split(' ')[1]} information.`}
                        modalHeader={"Beneficiary Client"}
                        clientOptions={clientOptions}
                        onClientSelect={(beneficiaryClientAccountsInfos) => applyBeneficiaryValues(beneficiaryClientAccountsInfos, header)}
                    />
                }
                <div className="user-inputs">
                    {categorySection[header].map(input => createInputField(input))}
                </div>
            </>
            
        )
    }

    const createInputField = (input) => {
        let inputs = clientOrAccountsHeaderValue === "CI" ? clientAccountsInfos?.clientFormDataSet : 
            clientAccountsInfos?.accounts.filter(account => account.accountId === clientOrAccountsHeaderValue)[0]?.accountFormDataSet
        let accountPlatform = getFormDataSetFieldValue(inputs, "Account Platform")

        return <InputField infoField={input} 
                    broker={broker}
                    handleInputChange={onInputChange} 
                    clientOrAccountsHeaderValue={clientOrAccountsHeaderValue} 
                    clientOptions={clientOptions}
                    accountPlatform={accountPlatform}
                    fieldOptions={fieldOptions}
                />
    }

    const isBeneficiarySection = (header) => {
        const splitHeader = header.split(' ')
        const isValidBeneficiaryNumber = !isNaN(splitHeader[1]) && parseInt(splitHeader[1]) >= 1 && parseInt(splitHeader[1]) <= 8

        return splitHeader[0] === 'Beneficiary' &&  isValidBeneficiaryNumber
    }

    const deleteClientAccount = () => {
        if(clientOrAccountsHeaderValue === 'CI') {
            handleDeleteClient(selectedClient.clientId)
        } else {
            handleDeleteAccount(clientOrAccountsHeaderValue, selectedClient.clientId)
        }

        setClientOrAccountsHeaderValue("CI")
    }

    const handleGenerateFormModalClose = () => {
        setGenerateFormModalOpen(false)
        refreshClientAccountsInfo(selectedClient.clientId)
    }

    return (
        <div className="file-details-inputs">
            <div className="inputs-container">
                <div className="inputs-header">
                    <div className="inputs-header-client-account">
                        <div className="client-accounts-select">
                            <Tabs activeKey={clientOrAccountsHeaderValue} onChange={handleClientOrAccountsHeaderSelect} items={createClientOrAccountsHeaderSelect()} />
                        </div>
                        {selectedClient &&
                            <div>
                                <Popconfirm
                                    title="Delete"
                                    description={`Are you sure to delete this ${clientOrAccountsHeaderValue === 'CI' ? 'client' : 'account'}?`}
                                    onConfirm={() => deleteClientAccount()}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <button className="delete-client-account-button">{`Delete ${clientOrAccountsHeaderValue === 'CI' ? 'Client' : 'Account'}`}</button>
                                </Popconfirm>
                            </div>
                        }
                    </div>
                    {getInputFilterSwitch()}
                </div>
                {createInputs()}

                <div className="input-form-button">
                    {role === "ADVISOR" && selectedClient && clientOrAccountsHeaderValue && clientOrAccountsHeaderValue !== 'CI' &&
                        <div className="input-form-sub-button">
                            <Button onClick={() => setGenerateFormModalOpen(true)}>
                                <span>Generate Form</span>
                                <FormOutlined  />
                            </Button>
                        </div>
                    }
                    {role === "ADVISOR" && selectedClient &&
                        <div className="input-form-sub-button">
                            <Button onClick={() => setRequestDocuSignFormModalOpen(true)}>
                                <span>Request Docusign</span>
                                <FileTextOutlined  />
                            </Button>
                        </div>
                    }
                    {clientAccountsInfos &&
                        <div className="input-form-update-button">
                            <Button loading={isUpdating} onClick={() => updateFileRef.current.click()}>
                                <span>{isUpdating ? "Updating" : "Update"}</span>
                                <EditOutlined  />
                            </Button>
                        </div>
                    }
                </div>
            </div>

            {generateFormModalOpen && 
                <CrmFormGeneratorModal
                    crmId={crmId}
                    clientId={selectedClient.clientId} 
                    accountId={clientOrAccountsHeaderValue}
                    isModalOpen={generateFormModalOpen}
                    onClose={handleGenerateFormModalClose}
                />
             }

            {isRequestDocuSignFormModalOpen &&
                <FormDocuSignIndividualRequest
                    crmId={crmId}
                    clientId={selectedClient.clientId} 
                    openModal={isRequestDocuSignFormModalOpen}
                    handleModalOpen={setRequestDocuSignFormModalOpen}
                />
            }
        </div>
    )
}

export default CrmInputs